import { Grid, Box } from "@mui/material";
import { useEffect, useContext } from "react";
import { useFormContext } from "react-hook-form";
import { get } from "lodash";
import { useTranslation } from "react-i18next";

import { CompanyDropdownWithoutLoader } from "../../CompanyDropdownWithoutLoader";
import AssetDropdown from "../../data/Assets/AssetDropdown/AssetDropdown";
import { useAssetsBySubstation } from "../../data/Assets/useAssetsBySubstation";
import { HVSubstationDropdown } from "../../HVSubstationDropdown";
import { DeleteButton } from "../../shared/FormLayout/IconButtons/DeleteButton";
import { Asset } from "../../shared/interfaces/Asset";
import { WorkflowV1 } from "../../shared/interfaces/WorkflowV1";
import LabelText from "../../shared/LabelValueFormatters/LabelText";
import { SiteDropdown } from "../../SiteDropdown";
import { WorkflowDropdown } from "../../WorkflowDropdown";
import { BulkWorkorderContext } from "./BulkWorkorderContext";
import { WorkorderLocationAndWorkflowGroup } from "./BulkWorkorderContext";
import { useCompaniesByCustomerId } from "../../data/useCompaniesByCustomerId";
import { WorkflowV2 } from "../../shared/interfaces/WorkflowV2";

interface WorkorderRowProps {
  group: WorkorderLocationAndWorkflowGroup;
  index: number;
}

export function WorkorderRow({ group, index }: WorkorderRowProps) {
  const { t } = useTranslation();

  const {
    workorderDetails,
    editGroupSubstation,
    editGroupSwitchgear,
    editGroupWorkflow,
    editGroupCompanyNames,
    editGroupSiteNames,
    removeLocationAndWorkflowGroup,
    editGroupAssets,
    assetsByGroupId,
  } = useContext(BulkWorkorderContext);

  const { register, formState } = useFormContext();
  const { errors } = formState;

  const { groupId, locationAndWorkflow, companyName, siteName, companyId, siteId } = group;

  const { data: assetsForSubstation } = useAssetsBySubstation(locationAndWorkflow.substationId);

  const companies = useCompaniesByCustomerId(workorderDetails.customerId);

  useEffect(() => {
    const currentAssets = assetsByGroupId[groupId] ?? [];

    const isSame =
      currentAssets.length === (assetsForSubstation?.length ?? 0) &&
      currentAssets.every((a, i) => a.id === assetsForSubstation?.[i]?.id);

    if (!isSame && assetsForSubstation) {
      editGroupAssets(groupId, assetsForSubstation);
    }
  }, [assetsForSubstation, assetsByGroupId, groupId, editGroupAssets]);

  const substationValidationKey = `locationsAndWorkflows.${index}.locationAndWorkflow.substationId`;
  const switchgearValidationKey = `locationsAndWorkflows.${index}.locationAndWorkflow.assetId`;
  const workflowValidationKey = `locationsAndWorkflows.${index}.locationAndWorkflow.workflowId`;
  const requiredKey = "required";

  register(substationValidationKey, {
    required: true,
    value: locationAndWorkflow.substationId,
  });
  register(switchgearValidationKey, {
    required: true,
    value: locationAndWorkflow.assetIds,
  });
  register(workflowValidationKey, {
    required: true,
    value: locationAndWorkflow.workflowId,
  });

  function hasRequiredFieldError(validationKey: string) {
    return get(errors, validationKey)?.type === requiredKey;
  }

  function handleCompanyChange(name?: string, id?: number) {
    editGroupCompanyNames(groupId, name, id);
    editGroupSiteNames(groupId, undefined, undefined);
    editGroupSubstation(groupId, undefined, undefined);
    editGroupSwitchgear(groupId, undefined);
    editGroupWorkflow(groupId, undefined);
  }

  function handleSiteChange(name?: string, id?: number) {
    editGroupSiteNames(groupId, name, id);
    editGroupSubstation(groupId, undefined, undefined);
    editGroupSwitchgear(groupId, undefined);
    editGroupWorkflow(groupId, undefined);
  }

  function onAssetChange(selectedAssets: Asset[]) {
    if (selectedAssets.length > 0) {
      editGroupSwitchgear(
        groupId,
        selectedAssets.map((a) => ({ assetId: a.id, assetName: a.name })),
      );
      editGroupWorkflow(groupId, undefined);
    } else {
      editGroupSwitchgear(groupId, undefined);
      editGroupWorkflow(groupId, undefined);
    }
  }

  const selectedAssets = (assetsByGroupId[groupId] ?? []).filter((asset) =>
    locationAndWorkflow.assetIds?.includes?.(asset.id),
  );

  return (
    <Grid container alignItems="center" justifyContent="space-around">
      <Grid item xs={11} lg={1} order={{ xs: 1, lg: 0 }}>
        <LabelText>{`${t("workorders.location")} ${index + 1}:`}</LabelText>
      </Grid>

      {companies.data?.length !== 0 && (
        <Grid item xs={12} md={2} px={{ lg: 2 }} py={{ xs: 1, md: 0 }} order={{ xs: 2, lg: 1 }}>
          <LabelText>{t("common.company")}</LabelText>
          <CompanyDropdownWithoutLoader
            customerId={workorderDetails.customerId}
            selectedCompanyName={companyName}
            onCompanyChange={(name, id) => handleCompanyChange(name, id)}
            clearAuto
            companies={companies.data}
            width="100%"
          />
        </Grid>
      )}

      {companies.data?.length !== 0 && (
        <Grid item xs={12} md={2} px={{ lg: 2 }} py={{ xs: 1, md: 0 }} order={{ xs: 3, lg: 2 }}>
          <LabelText>{t("common.site")}</LabelText>
          <SiteDropdown
            companyId={companyId}
            customerId={workorderDetails.customerId}
            selectedSiteName={siteName}
            onSiteChange={(name, id) => handleSiteChange(name, id)}
            clearAuto
            width="100%"
          />
        </Grid>
      )}

      <Grid item xs={12} md={2} px={{ lg: 2 }} py={{ xs: 1, md: 0 }} order={{ xs: 4, lg: 3 }}>
        <LabelText>{t("substations.substation")}</LabelText>
        <HVSubstationDropdown
          selectedSubstationId={locationAndWorkflow.substationId}
          customerId={workorderDetails.customerId}
          onSubstationChange={(substationId, substationName) => {
            editGroupSubstation(groupId, substationId, substationName);
            editGroupSwitchgear(groupId, undefined);
            editGroupWorkflow(groupId, undefined);
          }}
          invalid={hasRequiredFieldError(substationValidationKey)}
          invalidMessage={t("common.requiredFieldValidationError", {
            val: "$t(substations.substation)",
          })}
          companyId={companyId}
          siteId={siteId}
          key={`dropdown-substationId-${groupId}`}
          width="100%"
        />
      </Grid>

      <Grid item xs={12} md={2} px={{ lg: 2 }} py={{ xs: 1, md: 0 }} order={{ xs: 5, lg: 4 }}>
        <LabelText>{t("asset.assets")}</LabelText>
        <AssetDropdown
          multiselect
          onAssetChange={onAssetChange}
          invalidMessage={t("asset.empty", { val: t("asset.asset") })}
          invalid={hasRequiredFieldError(switchgearValidationKey)}
          width="100%"
          disableErrorSpace
          options={assetsByGroupId[groupId] ?? []}
          selectedAssets={selectedAssets}
        />
      </Grid>

      <Grid item xs={12} md={2} px={{ lg: 2 }} py={{ xs: 1, md: 0 }} order={{ xs: 6, lg: 5 }}>
        <Box width="100%" display="flex" flexDirection="column">
          <LabelText>{t("workflows.workflow")}</LabelText>
          <WorkflowDropdown
            customerId={workorderDetails.customerId}
            substationId={locationAndWorkflow.substationId}
            onWorkflowChange={(workflow: WorkflowV1 | WorkflowV2) => {
              if (locationAndWorkflow.workflowId !== workflow.id) {
                editGroupWorkflow(groupId, workflow);
              }
            }}
            selectedWorkflowId={locationAndWorkflow.workflowId}
            selectedAssets={selectedAssets}
            width="100%"
            invalid={hasRequiredFieldError(workflowValidationKey)}
            invalidMessage={t("common.requiredFieldValidationError", {
              val: "$t(workflows.workflow)",
            })}
            key={`dropdown-workflowId-${groupId}`}
          />
        </Box>
      </Grid>

      <Grid item order={{ xs: 0, lg: 6 }}>
        <DeleteButton onClick={() => removeLocationAndWorkflowGroup(groupId)} />
      </Grid>
    </Grid>
  );
}
